import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ScoreStatus, Sport } from '@sorare/core/src/__generated__/globalTypes';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { HeadlineM, LabelS } from '@sorare/core/src/atoms/typography';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { isGameStarted } from '@sorare/marketplace/src/shared/game/lib';
import { PlayerRotowireStatsProjection } from '@sorare/marketplace/src/shared/player/PlayerDetails/PlayerStats/PlayerRotowireStatsProjection';

import type {
  PlayerDetailsHeaderStats_anyPlayerInterface,
  PlayerDetailsHeaderStats_playerGameScoreInterface,
} from './__generated__/index.graphql.ts';

const Root = styled(SBHorizontal)`
  align-items: stretch;

  > ${Vertical} {
    justify-content: space-between;
  }
`;

type Props = {
  anyPlayer: PlayerDetailsHeaderStats_anyPlayerInterface;
  eligiblePlayerGameScores?: PlayerDetailsHeaderStats_playerGameScoreInterface[];
};

export const PlayerDetailsHeaderStats = ({
  anyPlayer,
  eligiblePlayerGameScores = [],
}: Props) => {
  const [playerGameScore] = eligiblePlayerGameScores;

  const totalScore = playerGameScore.score;

  let stats: {
    label: MessageDescriptor;
    score: ReactNode;
  }[] = [];

  if (anyPlayer.sport === Sport.FOOTBALL) {
    const decisiveScore =
      isType(playerGameScore, 'PlayerGameScore') ||
      isType(playerGameScore, 'So5Score')
        ? playerGameScore.decisiveScore?.totalScore
        : null;

    const hasPlayerScoreYet =
      isGameStarted(playerGameScore.anyGame.status) &&
      playerGameScore.anyPlayerGameStats.playedInGame &&
      typeof decisiveScore === 'number';

    stats = [
      {
        label: fantasy.decisiveScore,
        score: hasPlayerScoreYet ? (
          <Score
            score={decisiveScore}
            scoreStatus={ScoreStatus.FINAL}
            shape="TEXT"
          />
        ) : (
          '-'
        ),
      },
      {
        label: fantasy.allAroundScore,
        score: hasPlayerScoreYet ? (
          <Score
            score={totalScore - decisiveScore}
            scoreStatus={ScoreStatus.FINAL}
            shape="TEXT"
          />
        ) : (
          '-'
        ),
      },
      {
        label: fantasy.total,
        score: hasPlayerScoreYet ? (
          <Score
            score={totalScore}
            scoreStatus={ScoreStatus.FINAL}
            shape="TEXT"
          />
        ) : (
          '-'
        ),
      },
    ];
  }

  if (anyPlayer.sport === Sport.BASEBALL) {
    stats = [
      {
        label: fantasy.projectedScore,
        score: (
          <PlayerRotowireStatsProjection
            player={anyPlayer}
            positions={anyPlayer.anyPositions}
            playerGameScores={eligiblePlayerGameScores}
            variant="text"
          />
        ),
      },
      {
        label: fantasy.playerScore,
        score: (
          <Score
            score={totalScore}
            scoreStatus={ScoreStatus.FINAL}
            shape="TEXT"
          />
        ),
      },
    ];
  }
  return (
    <Root>
      <>
        {stats.map(stat => (
          <Vertical key={stat.label.id} gap={0.5}>
            <LabelS bold color="var(--c-nd-600)">
              <FormattedMessage {...stat.label} />
            </LabelS>
            <Horizontal>
              <HeadlineM>{stat.score}</HeadlineM>
            </Horizontal>
          </Vertical>
        ))}
      </>
    </Root>
  );
};

PlayerDetailsHeaderStats.fragments = {
  anyPlayerInterface: gql`
    fragment PlayerDetailsHeaderStats_anyPlayerInterface on AnyPlayerInterface {
      slug
      sport
      anyPositions
      ...PlayerRotowireStatsProjection_AnyPlayerInterface
    }
    ${PlayerRotowireStatsProjection.fragments.anyPlayerInterface}
  ` as TypedDocumentNode<PlayerDetailsHeaderStats_anyPlayerInterface>,
  playerGameScoreInterface: gql`
    fragment PlayerDetailsHeaderStats_playerGameScoreInterface on PlayerGameScoreInterface {
      id
      score
      anyPlayerGameStats {
        id
        playedInGame
      }
      ... on PlayerGameScore {
        id
        decisiveScore {
          totalScore
        }
      }
      ... on So5Score {
        id
        decisiveScore {
          totalScore
        }
      }
      anyGame {
        id
        status: statusTyped
      }
      ...PlayerRotowireStatsProjection_PlayerGameScoreInterface
    }
    ${PlayerRotowireStatsProjection.fragments.playerGameScoreInterface}
  ` as TypedDocumentNode<PlayerDetailsHeaderStats_playerGameScoreInterface>,
};
